import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, Hint, ShowMoreText, Text, Toggle } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { EMPTY_FIELD_VALUE, SHORT_DATE_FORMAT } from '@/common/constants';
import FilePreview from '@/modules/MyDocuments/components/FilePreview/FilePreview';
import { LettersOfMedicalNecessity } from '@/modules/MyDocuments/myDocuments.types';

interface Props {
  letterOfMedicalNecessity: LettersOfMedicalNecessity;
  fileUrl?: string;
  isSelected?: boolean;
  isLinked?: boolean;
  onSelect: (lmnId: string) => void;
  isMobile?: boolean;
}

const TransactionLinkedLmnListItem: React.FC<Props> = ({
  letterOfMedicalNecessity,
  fileUrl,
  isSelected,
  isLinked,
  onSelect,
  isMobile,
}) => {
  const { t } = useTranslation();
  const dates = useMemo(() => {
    const validFrom = letterOfMedicalNecessity?.validFrom;
    const validTo = letterOfMedicalNecessity?.validTo;
    if (!validFrom && !validTo) {
      return EMPTY_FIELD_VALUE;
    }
    const formattedValidFrom = validFrom ? dayjs(validFrom).format(SHORT_DATE_FORMAT) : EMPTY_FIELD_VALUE;
    const formattedValidTo = validTo ? dayjs(validTo).format(SHORT_DATE_FORMAT) : EMPTY_FIELD_VALUE;
    return `${formattedValidFrom} - ${formattedValidTo}`;
  }, [letterOfMedicalNecessity?.validFrom, letterOfMedicalNecessity?.validTo]);

  const toggleSelect = useCallback(() => {
    if (letterOfMedicalNecessity?.id) {
      onSelect(letterOfMedicalNecessity?.id);
    }
  }, [letterOfMedicalNecessity?.id, onSelect]);

  return (
    <Box
      data-testId="transaction-lmn-linked-list-item"
      margin={{ bottom: 'spacing12' }}
      pad={isMobile ? "spacing12" : { horizontal: 'spacing24', vertical: 'spacing16' }}
      background="canvas"
      direction="row"
      round="container2Round"
    >
      <FilePreview
        fileUrl={fileUrl}
        width={{ min: isMobile ? '44px' : '72px', width: isMobile ? '44px': '72px' }}
        height={{ min: isMobile ? '62px': '96px', height: isMobile ? '62px' : '96px' }}
      />

      <Box flex margin={{ left: isMobile ? 'spacing12' : 'spacing24' }}>
        <Box>
          <Text
            size="large"
            weight={700} 
            data-testId="transaction-lmn-linked-list-item_name"
          >
            {letterOfMedicalNecessity?.name}
          </Text>
          <Text
            size="small"
            weight={500}
            color="textSecondary"
            data-testId="transaction-lmn-linked-list-item_valid-dates"
          >
            Valid: {dates}
          </Text>
        </Box>

        <Box margin={{ top: 'spacing8' }}>
          <ShowMoreText
            testId="transaction-lmn-linked-list-item_description"
            lines={2}
            text={letterOfMedicalNecessity?.description}
          />
        </Box>

        {isMobile ? (
          <Box direction="row" margin={{ top: 'spacing12' }} width="100%">
            <AppButton
              testId="transaction-lmn-list-item_link-button"
              buttonType="secondary"
              onClick={toggleSelect}
              disabled={isLinked}
              width="100%"
            >
              {isSelected || isLinked ? t('Unlink from This Claim') : t('Link to This Claim')}
            </AppButton>
          </Box>
        ) : null}
      </Box>

      {isMobile ? null : (
        <Box alignSelf="center" margin={{ left: 'spacing8' }}>
          <Hint
            hideHint={!isLinked}
            hintWidth={168}
            hintAlign="vertical"
            hintElement={(
              <Box direction="row" >
                {isSelected || isLinked ? (
                  <Text data-testId="transaction-lmn-list-item_selected-text" color="textSecondary">
                    Selected
                  </Text>
                ) : null}
                <Box margin={{ left: 'spacing8' }}>
                  <Toggle
                    testId="transaction-lmn-list-item"
                    name="lmn"
                    value={Boolean(isSelected || isLinked)}
                    onChange={toggleSelect}
                    disabled={isLinked}
                  />
                </Box>
              </Box>
            )}
          >
            <Text size="small" textAlign="center">
              {t('This LMN is already attached')}
            </Text>
          </Hint>
        </Box>
      )}
    </Box>
  );
};

export default TransactionLinkedLmnListItem;
